// @ts-nocheck
/**
 * Agent Idle Stats — redesigned to obd3-gui pattern (FillPage + PageHeader
 * + bordered filter Card + shadcn DataTable). SUPERADMIN-only; non-super
 * viewers redirect to /dashboard.
 * v1 quirks preserved verbatim (double mount fetch, commented-out clamp
 * variant, Hinglish comment, React.memo wrap).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Filter, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import Loader from '@/components/partials/components/Loader';
import { getApiCall } from '@/service/httpCall';
import { API_AGENT_IDLE_STATS_GET } from '@/service/url';
import { ROUTES, USER_ROLES } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import { CalendarDateInput, CalendarTimeInput } from '@/components/ui/calendar-inputs';

const getTodayStr = () => new Date().toLocaleDateString('en-CA');
const getFirstOfMonthStr = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString('en-CA');
};
const getSixMonthsAgoStr = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d.toLocaleDateString('en-CA');
};

const normalizeIdleStats = (resp: any) => {
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.rows)) return resp.rows;
  return [];
};

const safeDate = (value: any) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const formatDuration = (seconds: any) => {
  const s = Math.max(0, Number(seconds) || 0);
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const remSeconds = s % 60;
  const pad2 = (v: any) => String(v).padStart(2, '0');
  if (hours > 0) return `${hours}:${pad2(minutes)}:${pad2(remSeconds)}`;
  return `${minutes}:${pad2(remSeconds)}`;
};

const AgentIdleStatsList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [userRole, setUserRole] = useState<any>(null);
  const [searchValue, setSearchValue] = useState('');
  const [fromDateValue, setFromDateValue] = useState(getFirstOfMonthStr());
  const [toDateValue, setToDateValue] = useState(getTodayStr());
  const [fromTimeValue, setFromTimeValue] = useState('');
  const [toTimeValue, setToTimeValue] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<any>({
    from: getFirstOfMonthStr(),
    to: getTodayStr(),
    fromTime: '',
    toTime: '',
  });

  const fetchIdleStats = async (
    start: any = fromDateValue,
    end: any = toDateValue,
  ) => {
    if (userRole !== USER_ROLES.SUPERADMIN && userRole !== null) return;
    setLoading(true);
    const queryParams = new URLSearchParams();
    if (start) queryParams.append('start_date', start);
    if (end) queryParams.append('end_date', end);
    const finalUrl = `${API_AGENT_IDLE_STATS_GET}${
      queryParams.toString() ? '?' + queryParams.toString() : ''
    }`;

    try {
      const resp: any = await getApiCall(finalUrl, {});
      setRows(normalizeIdleStats(resp));
    } catch (error) {
      console.error('Failed to fetch agent idle stats', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = () => {
    fetchIdleStats(fromDateValue, toDateValue);
    // Table ko batayein ki in dates par filter kare
    setAppliedFilters({
      from: fromDateValue,
      to: toDateValue,
      fromTime: fromTimeValue,
      toTime: toTimeValue,
    });
  };

  useEffect(() => {
    let isMounted = true;
    const user = getUser();
    if (!isMounted) return;
    setUserRole(user?.role || null);
    if (user?.role !== USER_ROLES.SUPERADMIN) {
      navigate(`/${ROUTES.DASHBOARD}`, { replace: true });
    } else {
      fetchIdleStats(getFirstOfMonthStr(), getTodayStr());
    }
    return () => {
      isMounted = false;
    };
  }, [navigate]);

  useEffect(() => {
    if (userRole !== USER_ROLES.SUPERADMIN) return;
    let isMounted = true;
    setLoading(true);
    getApiCall(API_AGENT_IDLE_STATS_GET)
      .then((resp: any) => {
        if (!isMounted) return;
        setRows(normalizeIdleStats(resp));
      })
      .catch((error: any) => {
        console.error('Failed to fetch agent idle stats', error);
        if (!isMounted) return;
        setRows([]);
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [userRole]);

  const tableRows = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return list
      .map((r: any, idx: number) => {
        const fromDate = safeDate(r?.from);
        const toDate = safeDate(r?.to);
        const durationSeconds =
          fromDate && toDate
            ? Math.max(0, Math.floor((toDate.getTime() - fromDate.getTime()) / 1000))
            : 0;
        const agentName =
          r?.agent_name ||
          r?.agent?.agent_name ||
          r?.agent?.name ||
          r?.agent?.full_name ||
          r?.agent?.username ||
          '';
        return {
          key: r?.id || idx + 1,
          id: r?.id,
          agent_id: r?.agent_id,
          agent_name: agentName,
          from: fromDate ? fromDate.toLocaleString() : '',
          to: toDate ? toDate.toLocaleString() : '',
          _fromTs: fromDate ? fromDate.getTime() : 0,
          _toTs: toDate ? toDate.getTime() : 0,
          durationSeconds,
          duration: formatDuration(durationSeconds),
        };
      })
      .sort((a: any, b: any) => (b._toTs || 0) - (a._toTs || 0));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    const start = appliedFilters.from
      ? new Date(
          `${appliedFilters.from}T${appliedFilters.fromTime || '00:00'}:00.000`,
        ).getTime()
      : null;
    const end = appliedFilters.to
      ? new Date(
          `${appliedFilters.to}T${appliedFilters.toTime || '23:59'}:59.999`,
        ).getTime()
      : null;

    return tableRows.filter((r: any) => {
      if (query) {
        const agentId = r?.agent_id != null ? String(r.agent_id).toLowerCase() : '';
        const agentName = (r?.agent_name || '').toLowerCase();
        if (!agentId.includes(query) && !agentName.includes(query)) return false;
      }
      // if (start != null && (r?._fromTs || 0) < start) return false;
      // if (end != null && (r?._toTs || 0) > end) return false;
      if (start != null && (r?._toTs || 0) < start) return false;
      if (end != null && (r?._fromTs || 0) > end) return false;
      return true;
    });
  }, [searchValue, tableRows, appliedFilters]);

  const durationToneClass = (s: number) => {
    if (s >= 3600)
      return 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300';
    if (s >= 900)
      return 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300';
    return 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300';
  };

  if (loading) return <Loader />;

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'agent_id',
      header: 'Agent ID',
      size: 100,
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.original.agent_id ?? '—'}
        </span>
      ),
    },
    {
      accessorKey: 'agent_name',
      header: 'Agent',
      cell: ({ row }) => (
        <span className="font-medium text-foreground">
          {row.original.agent_name || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'from',
      header: 'From',
      cell: ({ row }) => (
        <span className="font-mono text-xs">{row.original.from || '—'}</span>
      ),
    },
    {
      accessorKey: 'to',
      header: 'To',
      cell: ({ row }) => (
        <span className="font-mono text-xs">{row.original.to || '—'}</span>
      ),
    },
    {
      accessorKey: 'duration',
      header: 'Duration',
      size: 120,
      cell: ({ row }) => (
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums ring-1 ring-inset ${durationToneClass(
            row.original.durationSeconds,
          )}`}
        >
          {row.original.duration}
        </span>
      ),
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="Agent Idle Stats"
        description="Per-agent idle intervals across the selected date and time range."
      />

      <div
        className="mb-3 rounded-xl border border-border/60 bg-card px-3 py-2.5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[180px] flex-1 space-y-0.5">
            <Label htmlFor="search" className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Search
            </Label>
            <input
              id="search"
              type="text"
              placeholder="ID or name…"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="input-embossed h-9 w-full rounded-md px-2.5 text-xs"
            />
          </div>
          <div className="space-y-0.5">
            <Label htmlFor="from_date" className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              From date
            </Label>
            <CalendarDateInput
              id="from_date"
              value={fromDateValue}
              onChange={(e) => setFromDateValue(e)}
              min={getSixMonthsAgoStr()}
              max={getTodayStr()}
              className="input-embossed h-9 w-[140px] rounded-md px-2.5 text-xs"
            />
          </div>
          <div className="space-y-0.5">
            <Label htmlFor="to_date" className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              To date
            </Label>
            <CalendarDateInput
              id="to_date"
              value={toDateValue}
              onChange={(e) => setToDateValue(e)}
              min={fromDateValue}
              max={getTodayStr()}
              className="input-embossed h-9 w-[140px] rounded-md px-2.5 text-xs"
            />
          </div>
          <div className="space-y-0.5">
            <Label htmlFor="from_time" className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              From time
            </Label>
            <CalendarTimeInput
              id="from_time"
              value={fromTimeValue}
              onChange={(e) => setFromTimeValue(e)}
              className="input-embossed h-9 w-[110px] rounded-md px-2.5 text-xs"
            />
          </div>
          <div className="space-y-0.5">
            <Label htmlFor="to_time" className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              To time
            </Label>
            <CalendarTimeInput
              id="to_time"
              value={toTimeValue}
              onChange={(e) => setToTimeValue(e)}
              className="input-embossed h-9 w-[110px] rounded-md px-2.5 text-xs"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleFilterClick}>
              <Filter className="h-3.5 w-3.5" />
              Apply
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const start = getFirstOfMonthStr();
                const end = getTodayStr();
                setSearchValue('');
                setFromDateValue(start);
                setToDateValue(end);
                setFromTimeValue('');
                setToTimeValue('');
                setAppliedFilters({ from: start, to: end, fromTime: '', toTime: '' });
                fetchIdleStats(start, end);
              }}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Clear
            </Button>
          </div>
        </div>
      </div>

      <DataTable
        tableId="agent-idle-stats:list:v1"
        columns={columns}
        data={filteredRows}
        pageSize={10}
        emptyTitle="No idle stats"
        emptyDescription="No idle intervals match the current filters."
        getRowId={(row: any) => String(row.key)}
        disableExport
      />
    </FillPage>
  );
};

export default React.memo(AgentIdleStatsList);
